import { useState, useEffect, useCallback, useRef } from 'react';
import { condoApi } from '../services/api';
import { logger } from '../services/logger';

/**
 * Customer note type - represents an operator note/alert for a customer
 * Notes can be at customer level (global) or unit/department/person level (hierarchical)
 */
export interface CustomerNote {
  id: string;
  customer_id: string;
  person_id?: string;      // For person-specific notes
  person_name?: string;    // Resolved person name from backend
  unit_id?: string;        // For unit-specific notes (condominios)
  department_id?: string;  // For department-specific notes (empresas)
  unit_name?: string;      // Resolved unit name from backend
  department_name?: string; // Resolved department name from backend
  content: string;
  photo_url?: string;      // Optional photo attachment (livro notes)
  is_active: boolean;
  created_by_id: string;
  created_by_name: string;
  created_at: string;
  concluded_by_id?: string;
  concluded_by_name?: string;
  concluded_at?: string;
  concluded_reason?: string;
}

/**
 * Options for filtering notes by hierarchical scope
 */
export interface NoteScope {
  person_id?: string;      // For person-specific notes
  unit_id?: string;        // For condominios - filter by unit
  department_id?: string;  // For empresas - filter by department
}

interface UseCustomerNotesResult {
  notes: CustomerNote[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  createNote: (content: string, photoUrl?: string) => Promise<void>;
  concludeNote: (noteId: string, reason?: string) => Promise<void>;
  refreshNotes: () => Promise<void>;
}

interface UseCustomerNotesOptions {
  scope?: NoteScope;
  activeOnly?: boolean;
  noteType?: string; // "aviso" (default) or "livro"
}

/**
 * Hook for managing customer notes (operator alerts/notes)
 * Fetches notes for a customer and provides CRUD operations
 *
 * Supports hierarchical filtering:
 * - If scope.person_id is provided: shows only notes for that person
 * - If scope.unit_id is provided: shows only notes for that unit (condominios)
 * - If scope.department_id is provided: shows only notes for that department (empresas)
 * - If no scope is provided: shows only global notes (customer-level)
 *
 * @param customerId - The customer ID
 * @param options - Optional configuration including scope and activeOnly filter
 */
export const useCustomerNotes = (customerId: string | undefined, options?: UseCustomerNotesOptions | NoteScope): UseCustomerNotesResult => {
  // Support both old API (scope directly) and new API (options object)
  const scope = options && 'person_id' in options || options && 'unit_id' in options || options && 'department_id' in options
    ? options as NoteScope
    : (options as UseCustomerNotesOptions)?.scope;
  const activeOnly = (options as UseCustomerNotesOptions)?.activeOnly ?? true;
  const noteType = (options as UseCustomerNotesOptions)?.noteType;
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitiallyLoaded = useRef(false);

  // Use ref to store scope to prevent fetchNotes from being recreated on scope changes
  // This prevents the auto-refresh interval from restarting on every scope change
  const scopeRef = useRef(scope);
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);

  // Tracks the request currently in flight, keyed by customerId+scope+filters, so
  // overlapping calls (mount effect + scope effect, StrictMode double-invoke, etc.)
  // collapse into a single real HTTP request instead of firing one each.
  const inFlightRef = useRef<{ key: string; promise: Promise<void> } | null>(null);

  // Fetch active notes for the customer with optional hierarchical scope
  const fetchNotes = useCallback(async (isRefresh: boolean = false) => {
    if (!customerId) {
      setNotes([]);
      return;
    }

    // Build filter object based on current scope from ref
    const currentScope = scopeRef.current;
    const filters: { person_id?: string; unit_id?: string; department_id?: string } = {};
    if (currentScope?.person_id) {
      filters.person_id = currentScope.person_id;
    } else if (currentScope?.unit_id) {
      filters.unit_id = currentScope.unit_id;
    } else if (currentScope?.department_id) {
      filters.department_id = currentScope.department_id;
    }
    // If no scope, API returns global notes (where person_id, unit_id, and department_id are NULL)

    const requestKey = JSON.stringify({ customerId, filters, activeOnly, noteType });
    if (inFlightRef.current?.key === requestKey) {
      // Identical request already in flight — piggyback instead of firing a duplicate
      return inFlightRef.current.promise;
    }

    // Only set loading for initial load, use refreshing for background updates
    if (!isRefresh && !hasInitiallyLoaded.current) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    const doFetch = (async () => {
    try {
      logger.debug('useCustomerNotes', 'Fetching notes', { customerId, scope: filters, activeOnly, noteType, isRefresh });
      const response = await condoApi.getCustomerNotes(customerId, activeOnly, filters, noteType);

      if (response.data.success) {
        const newNotes = response.data.notes || [];
        // Only update state if data actually changed (compare by JSON)
        setNotes(prev => {
          const prevJson = JSON.stringify(prev);
          const newJson = JSON.stringify(newNotes);
          if (prevJson === newJson) {
            return prev; // No change, keep same reference to avoid re-render
          }
          return newNotes;
        });
        logger.debug('useCustomerNotes', 'Notes fetched successfully', {
          customerId,
          scope: filters,
          count: newNotes.length,
          isRefresh
        });
      } else {
        throw new Error('Failed to fetch notes');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch notes';
      logger.error('useCustomerNotes', 'Error fetching notes', err, { customerId, scope: scopeRef.current });
      setError(errorMsg);
    } finally {
      if (!hasInitiallyLoaded.current) {
        hasInitiallyLoaded.current = true;
      }
      setLoading(false);
      setRefreshing(false);
    }
    })();

    inFlightRef.current = { key: requestKey, promise: doFetch };
    try {
      await doFetch;
    } finally {
      if (inFlightRef.current?.key === requestKey) {
        inFlightRef.current = null;
      }
    }
  }, [customerId, activeOnly, noteType]);

  // Fetch notes on mount and when customerId changes
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Fetch notes when scope changes (person_id, unit_id, or department_id)
  useEffect(() => {
    // Skip initial mount (handled by fetchNotes effect above)
    if (hasInitiallyLoaded.current) {
      fetchNotes();
    }
  }, [scope?.person_id, scope?.unit_id, scope?.department_id, fetchNotes]);

  // Create a new note with the current hierarchical scope
  const createNote = useCallback(async (content: string, photoUrl?: string) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }

    // Build scope object for the note based on current filter scope from ref
    const currentScope = scopeRef.current;
    const noteScope: { person_id?: string; unit_id?: string; department_id?: string } = {};
    if (currentScope?.person_id) {
      noteScope.person_id = currentScope.person_id;
    } else if (currentScope?.unit_id) {
      noteScope.unit_id = currentScope.unit_id;
    } else if (currentScope?.department_id) {
      noteScope.department_id = currentScope.department_id;
    }
    // If no scope, creates a global note (customer-level)

    logger.debug('useCustomerNotes', 'Creating note', { customerId, contentLength: content.length, scope: noteScope, hasPhoto: !!photoUrl });

    try {
      const response = await condoApi.createCustomerNote(customerId, content, noteScope, noteType, photoUrl);

      if (response.data.success) {
        // Add the new note to the list (optimistic update)
        const newNote = response.data.data as CustomerNote;
        setNotes(prev => [newNote, ...prev]);
        logger.info('useCustomerNotes', 'Note created successfully', { customerId, noteId: newNote.id, scope: noteScope });
        // Auto-refresh will ensure consistency within 15 seconds
      } else {
        throw new Error(response.data.error || 'Failed to create note');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create note';
      logger.error('useCustomerNotes', 'Error creating note', err, { customerId, scope: noteScope });
      throw new Error(errorMsg);
    }
  }, [customerId, fetchNotes, noteType]);

  // Conclude (dismiss) a note
  const concludeNote = useCallback(async (noteId: string, reason?: string) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }

    logger.debug('useCustomerNotes', 'Concluding note', { customerId, noteId, hasReason: !!reason });

    try {
      const response = await condoApi.concludeCustomerNote(customerId, noteId, reason);

      if (response.data.success) {
        // Remove the note from active list (optimistic update)
        setNotes(prev => prev.filter(note => note.id !== noteId));
        logger.info('useCustomerNotes', 'Note concluded successfully', { customerId, noteId });
      } else {
        throw new Error(response.data.error || 'Failed to conclude note');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to conclude note';
      logger.error('useCustomerNotes', 'Error concluding note', err, { customerId, noteId });
      throw new Error(errorMsg);
    }
  }, [customerId]);

  /**
   * Stable identity, deliberately. Consumers put this in effect dependency arrays —
   * CustomerNotesBanner does exactly that — and a fresh arrow on every render made the effect
   * re-fire on its own result: fetch → setState → re-render → new identity → fetch. Creating an
   * aviso lit that fuse (refreshTrigger flips to 1) and the app then hammered
   * GET /notes?active=true for as long as the screen stayed open, flickering the row spinner with
   * every `refreshing` toggle.
   */
  const refreshNotes = useCallback(() => fetchNotes(true), [fetchNotes]);

  return {
    notes,
    loading,
    refreshing,
    error,
    createNote,
    concludeNote,
    refreshNotes,
  };
};
